import type { BulkImportChanges } from "@/types/bulk-import";

const LOCAL_STORAGE_IMPORT_DRAFT_KEY = "import.draft";
type FacilityDraft = {
  updated: Date;
  data: Map<string, BulkImportChanges>;
};

export const useImportFacilityStore = defineStore("import-facility", () => {
  /**
   * Indicate the current facility id even if the facility is not yet loaded
   */
  const activeImportFacilityId = ref<number>();

  /**
   * Map of pending changes by page id.
   * This is stored under the user command "Save Changes" to avoid losing data when navigating away from the page.
   */
  const draftSnapshotPerFacility = useLocalStorage(
    LOCAL_STORAGE_IMPORT_DRAFT_KEY,
    new Map<number, FacilityDraft>(),
    {
      deep: true,
      serializer: {
        read: (v) =>
          $ck.utils.tryParseJson(v, new Map<number, FacilityDraft>()),
        write: (v) =>
          $ck.utils.trySerializeJson($ck.utils.sanitizeMap(v), "[]"),
      },
    },
  );

  const unsavedChangesState = reactive({
    showLeaveConfirmation: false,
    confirmationLeaveRoute: undefined as string | undefined,
  });

  const fetchProductionLines = useGetProductionLines();
  const fetchWorkGroups = useGetWorkGroups();
  const fetchEquipmentAreas = useGetEquipmentAreas();
  const getManufacturersFetch = useGetManufacturersForImport();
  const fetchDailyDuties = useGetDailyDutiesForImport();
  const fetchGlobalTask = useGetGlobalTasks();
  const fetchTaskList = useGetTaskList();
  const fetchSkillList = useGetSkillList();
  const lockTaskList = useLockTasksForImport();
  const unlockTaskList = useUnlockTasksForImport();

  const isLoading = computed(
    () =>
      activeImportFacilityId.value !== undefined &&
      (fetchProductionLines.pending.value ||
        fetchWorkGroups.pending.value ||
        fetchEquipmentAreas.pending.value ||
        getManufacturersFetch.pending.value ||
        fetchDailyDuties.pending.value ||
        fetchGlobalTask.pending.value ||
        fetchTaskList.pending.value ||
        fetchSkillList.pending.value),
  );

  const productionLines = computed(() => fetchProductionLines.data.value ?? []);
  const workGroups = computed(() => fetchWorkGroups.data.value ?? []);
  const equipmentAreas = computed(() => fetchEquipmentAreas.data.value ?? []);
  const manufacturers = computed(
    () => getManufacturersFetch.data.value?.data ?? [],
  );
  const dailyDuties = computed(() => fetchDailyDuties.data.value ?? []);
  const globalBeforeTasks = computed(
    () => fetchGlobalTask.data.value?.globalBeforeTasks ?? [],
  );
  const globalAfterTasks = computed(
    () => fetchGlobalTask.data.value?.globalAfterTasks ?? [],
  );
  const taskList = computed(() => fetchTaskList.data.value?.tasks ?? []);
  const taskListLockInfo = computed(() => {
    return fetchTaskList.data.value;
  });

  /**
   * reset all the collections to their empty state.
   * This should be called when navigating away from import page.
   * Right now is called in the middleware for import.
   */
  const reset = () => {
    activeImportFacilityId.value = undefined;
    fetchProductionLines.reset();
    fetchWorkGroups.reset();
    fetchEquipmentAreas.reset();
    getManufacturersFetch.reset();
    fetchDailyDuties.reset();
    fetchGlobalTask.reset();
    fetchTaskList.reset();
    fetchSkillList.reset();
    unsavedChangesState.showLeaveConfirmation = false;
    unsavedChangesState.confirmationLeaveRoute = undefined;
  };

  const getProductionLines = () => fetchProductionLines.safeFetch();
  const getWorkGroups = () => fetchWorkGroups.safeFetch();
  const getEquipmentAreas = () => fetchEquipmentAreas.safeFetch();
  // TODO: fix underlying args issue
  const getManufacturers = () => getManufacturersFetch.safeFetch(undefined);
  const getDailyDuties = () => fetchDailyDuties.safeFetch();
  const getGlobalBeforeTasks = () =>
    fetchGlobalTask.safeFetch().then((data) => data?.globalBeforeTasks ?? []);
  const getGlobalAfterTasks = () =>
    fetchGlobalTask.safeFetch().then((data) => data?.globalAfterTasks ?? []);
  const getTaskList = () => fetchTaskList.safeFetch();
  const updateTaskListLock = (lock = true) =>
    lock ? lockTaskList.safeMutate() : unlockTaskList.safeMutate();

  const getSkillList = () => fetchSkillList.safeFetch();

  return {
    productionLines,
    workGroups,
    equipmentAreas,
    manufacturers,
    dailyDuties,
    globalBeforeTasks,
    globalAfterTasks,
    taskList,
    taskListLockInfo,
    isLoading,
    /**
     * Indicate the current facility id even if the facility is not yet loaded
     */
    activeImportFacilityId,
    draftSnapshotPerFacility,
    reset,
    getProductionLines,
    getWorkGroups,
    getEquipmentAreas,
    getManufacturers,
    getDailyDuties,
    getGlobalBeforeTasks,
    getGlobalAfterTasks,
    getTaskList,
    updateTaskListLock,
    getSkillList,
  };
});
